import { useEffect, useState } from "react";

import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { listOrg, postIntelRequestSubmission } from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useParams } from "react-router-dom";

import styles from "@/components/online-reporting/CISOReportSelect.module.scss";

interface Organization {
  id: string;
  title: string;
}

const RequestSubmissionPage = () => {
  const { orgId } = useParams();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [email, setEmail] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        const response = await listOrg({ throwOnError: false });
        if (response.status === 200 && response.data) {
          setOrganizations(
            response.data.sort((a: Organization, b: Organization) =>
              a.title.localeCompare(b.title),
            ),
          );
        } else {
          toast.error("Failed to load organizations");
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
        toast.error("Failed to load organizations");
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  const handleSubmit = async () => {
    if (!email || !firstName || !lastName || !selectedOrgId) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Basic email validation
    if (!email.includes("@") || !email.includes(".")) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      setSubmitting(true);
      const response = await postIntelRequestSubmission({
        body: {
          email: email.trim().toLowerCase(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          org_id: selectedOrgId,
        },
        throwOnError: false,
      });

      if (response.status === 200 && response.data) {
        toast.success(
          response.data.message || "Submission request sent successfully",
        );

        // Reset form
        setEmail("");
        setFirstName("");
        setLastName("");
        // Keep selectedOrgId or reset to current orgId if available
        if (orgId) {
          setSelectedOrgId(orgId);
        } else {
          setSelectedOrgId("");
        }
      } else {
        const errorMessage =
          response.error?.detail?.[0]?.msg ||
          "Failed to send submission request";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error sending request:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to send submission request",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <CircularProgress size={50} thickness={2} />
      </div>
    );
  }

  return (
    <div className={styles.selectContainer}>
      <div className={styles.headerContainer}>
        <Typography variant="h4" className={styles.title}>
          Request Submission
        </Typography>
      </div>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Request OCIS Submission
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Send a request to someone to submit a report in the OCIS area. If the
          user doesn't exist, they will be created with minimal permissions and
          receive an invite to set up their account.
        </Typography>

        <Grid container spacing={2}>
          <Grid size={12}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Email Address
            </Typography>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              helperText="Email address of the person to request submission from"
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              First Name
            </Typography>
            <TextField
              fullWidth
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Last Name
            </Typography>
            <TextField
              fullWidth
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Organization to add the user to
            </Typography>
            <FormControl fullWidth required>
              <InputLabel>Organization</InputLabel>
              <Select
                value={selectedOrgId}
                label="Organization"
                onChange={(e) => setSelectedOrgId(e.target.value)}
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={
                submitting ||
                !email ||
                !firstName ||
                !lastName ||
                !selectedOrgId
              }
              size="large"
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Send Submission Request"
              )}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default RequestSubmissionPage;
